// =============================================================================
// Admin Document Bulk Upload Page
// =============================================================================
// Upload multiple documents at once for a specific investor profile
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Card, Button } from '../../components/ui';
import { adminService, type DealWithStats, type InvestorWithProfiles } from '../../services/admin.service';
import { getApiError } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import type { DocumentType } from '../../types';

// =============================================================================
// Types
// =============================================================================

interface FileWithMetadata {
  file: File;
  id: string;
  type: DocumentType;
  name: string;
}

interface UploadResults {
  totalFiles: number;
  uploaded: number;
  errors: number;
  results: {
    uploaded: { id: string; name: string; filename: string }[];
    errors: { filename: string; error: string }[];
  };
}

// =============================================================================
// Component
// =============================================================================

export function DocumentBulkUploadPage() {
  const navigate = useNavigate();
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canUpload = hasPermission('documents', 'upload');
  const [searchParams] = useSearchParams();
  const preselectedProfileId = searchParams.get('profileId') || '';
  const preselectedDealId = searchParams.get('dealId') || '';

  // Data
  const [investors, setInvestors] = useState<InvestorWithProfiles[]>([]);
  const [deals, setDeals] = useState<DealWithStats[]>([]);
  const [loading, setLoading] = useState(true);

  // Selection
  const [selectedProfileId, setSelectedProfileId] = useState(preselectedProfileId);
  const [selectedDealId, setSelectedDealId] = useState(preselectedDealId);

  // Files
  const [files, setFiles] = useState<FileWithMetadata[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<UploadResults | null>(null);

  const documentTypes: { value: DocumentType; label: string }[] = [
    { value: 'QUARTERLY_REPORT', label: 'Quarterly Report' },
    { value: 'K1', label: 'K-1' },
    { value: 'PPM', label: 'PPM' },
    { value: 'SUBSCRIPTION_AGREEMENT', label: 'Subscription Agreement' },
    { value: 'FINANCIAL_STATEMENT', label: 'Financial Statement' },
    { value: 'OPERATING_AGREEMENT', label: 'Operating Agreement' },
    { value: 'OTHER', label: 'Other' },
  ];

  // Load investors and deals
  useEffect(() => {
    const loadData = async () => {
      try {
        const [investorsData, dealsData] = await Promise.all([
          adminService.listInvestors({ limit: 1000 }),
          adminService.listDeals(),
        ]);
        setInvestors(investorsData.investors);
        setDeals(dealsData);
      } catch (err) {
        setError(getApiError(err).message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Generate unique ID for file tracking
  const generateId = () => Math.random().toString(36).substring(2, 9);

  // Handle file selection
  const handleFilesSelect = useCallback((selectedFiles: FileList | File[]) => {
    const newFiles: FileWithMetadata[] = Array.from(selectedFiles).map(file => ({
      file,
      id: generateId(),
      type: 'OTHER' as DocumentType,
      name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
    }));
    setFiles(prev => [...prev, ...newFiles]);
    setError(null);
  }, []);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleFilesSelect(e.dataTransfer.files);
    }
  }, [handleFilesSelect]);

  // Handle file input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesSelect(e.target.files);
      e.target.value = ''; // Reset input
    }
  }, [handleFilesSelect]);

  // Update file metadata
  const updateFileType = (id: string, type: DocumentType) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, type } : f));
  };

  const updateFileName = (id: string, name: string) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, name } : f));
  };

  // Remove file
  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedProfileId || !selectedDealId || files.length === 0) {
      setError('Please select an investor profile, deal, and add at least one file');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const uploadResults = await adminService.uploadDocumentsBulk(
        files.map(f => f.file),
        {
          investorProfileId: selectedProfileId,
          dealId: selectedDealId,
          fileMetadata: files.map(f => ({
            filename: f.file.name,
            type: f.type,
            name: f.name,
          })),
        }
      );
      setResults(uploadResults);
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsUploading(false);
    }
  };

  // Reset for another upload
  const handleReset = () => {
    setFiles([]);
    setResults(null);
    setError(null);
  };

  // Get selected investor profile info for display
  const getSelectedProfileInfo = () => {
    for (const investor of investors) {
      const profile = investor.profiles.find(p => p.id === selectedProfileId);
      if (profile) {
        return {
          investorName: investor.name,
          investorEmail: investor.email,
          profileName: profile.entityName || profile.legalName || 'Individual',
          profileType: profile.type,
        };
      }
    }
    return null;
  };

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('documents', 'upload')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/admin/documents"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bulk Document Upload</h1>
          <p className="mt-1 text-sm text-gray-500">
            Upload multiple documents for a specific investor profile
          </p>
        </div>
      </div>

      {/* Results View */}
      {results ? (
        <div className="space-y-6">
          <Card padding="lg">
            <div className="text-center">
              {results.errors === 0 ? (
                <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto" />
              ) : (
                <XCircleIcon className="h-12 w-12 text-yellow-500 mx-auto" />
              )}
              <h2 className="mt-4 text-xl font-semibold text-gray-900">Upload Complete</h2>
              <p className="mt-2 text-gray-600">
                {results.uploaded} of {results.totalFiles} files uploaded successfully.
              </p>
            </div>

            {/* Stats Grid */}
            <div className="mt-6 grid grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-700">{results.totalFiles}</p>
                <p className="text-sm text-gray-600">Total Files</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{results.uploaded}</p>
                <p className="text-sm text-green-600">Uploaded</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-700">{results.errors}</p>
                <p className="text-sm text-red-600">Errors</p>
              </div>
            </div>

            {/* Uploaded Files */}
            {results.results.uploaded.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium text-gray-900 mb-2">Uploaded Files</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  {results.results.uploaded.map((f, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                      {f.filename}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Error Files */}
            {results.results.errors.length > 0 && (
              <div className="mt-6">
                <h3 className="font-medium text-gray-900 mb-2">Failed Files</h3>
                <ul className="text-sm text-red-600 space-y-1">
                  {results.results.errors.map((f, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <XCircleIcon className="h-4 w-4 text-red-500" />
                      {f.filename}: {f.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-6 flex justify-center gap-4">
              <Button
                variant="secondary"
                onClick={handleReset}
                className="flex items-center gap-2"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Upload More
              </Button>
              <Button
                onClick={() => navigate('/admin/documents')}
                className="flex items-center gap-2"
              >
                View Documents
              </Button>
            </div>
          </Card>
        </div>
      ) : (
        <>
          {/* Step 1: Select Investor Profile */}
          <Card padding="lg">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-primary-700">1</span>
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">Select Investor Profile</h2>
                <p className="mt-1 text-sm text-gray-600">
                  All documents will be assigned to this investor profile.
                </p>

                <div className="mt-4 max-w-lg">
                  <select
                    value={selectedProfileId}
                    onChange={(e) => setSelectedProfileId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select an investor profile...</option>
                    {investors.map(investor => (
                      <optgroup key={investor.id} label={`${investor.name} (${investor.email})`}>
                        {investor.profiles.map(profile => (
                          <option key={profile.id} value={profile.id}>
                            {profile.entityName || profile.legalName || 'Individual'} ({profile.type})
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {selectedProfileId && getSelectedProfileInfo() && (
                  <div className="mt-3 p-3 bg-primary-50 rounded-lg text-sm">
                    <p className="font-medium text-primary-900">
                      {getSelectedProfileInfo()?.profileName}
                    </p>
                    <p className="text-primary-700">
                      {getSelectedProfileInfo()?.investorName} ({getSelectedProfileInfo()?.investorEmail})
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Step 2: Select Deal */}
          <Card padding="lg">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-primary-700">2</span>
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">Select Deal</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Documents will be associated with this deal.
                </p>

                <div className="mt-4 max-w-lg">
                  <select
                    value={selectedDealId}
                    onChange={(e) => setSelectedDealId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select a deal...</option>
                    {deals.map(deal => (
                      <option key={deal.id} value={deal.id}>
                        {deal.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </Card>

          {/* Step 3: Upload Files */}
          <Card padding="lg">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-semibold text-primary-700">3</span>
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">Upload Documents</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Add files and set the document type for each.
                </p>

                {/* Error Message */}
                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {/* Drop Zone */}
                <label
                  className={`mt-4 border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer block ${
                    isDragging
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    multiple
                    onChange={handleInputChange}
                    className="hidden"
                  />
                  <DocumentArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto" />
                  <p className="mt-2 text-sm text-gray-600">
                    Drag and drop files here, or{' '}
                    <span className="text-primary-600 hover:text-primary-700">browse</span>
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    PDF, Word, Excel, PowerPoint, images
                  </p>
                </label>

                {/* File List */}
                {files.length > 0 && (
                  <div className="mt-6 space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900">{files.length} file(s) selected</h3>
                      <button
                        onClick={() => setFiles([])}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove all
                      </button>
                    </div>

                    <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
                      {files.map((fileData) => (
                        <div key={fileData.id} className="p-3 flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {fileData.file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(fileData.file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>

                          {/* Document Name */}
                          <input
                            type="text"
                            value={fileData.name}
                            onChange={(e) => updateFileName(fileData.id, e.target.value)}
                            placeholder="Document name"
                            className="w-48 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          />

                          {/* Document Type */}
                          <select
                            value={fileData.type}
                            onChange={(e) => updateFileType(fileData.id, e.target.value as DocumentType)}
                            className="w-40 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          >
                            {documentTypes.map(dt => (
                              <option key={dt.value} value={dt.value}>
                                {dt.label}
                              </option>
                            ))}
                          </select>

                          {/* Remove Button */}
                          <button
                            onClick={() => removeFile(fileData.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Button */}
                {files.length > 0 && (
                  <div className="mt-6 flex justify-end gap-3">
                    <Button
                      variant="secondary"
                      onClick={() => setFiles([])}
                      disabled={isUploading}
                    >
                      Clear All
                    </Button>
                    <Button
                      onClick={handleUpload}
                      disabled={isUploading || !selectedProfileId || !selectedDealId || !canUpload}
                      className="flex items-center gap-2"
                    >
                      {isUploading ? (
                        <>
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <DocumentArrowUpIcon className="h-4 w-4" />
                          Upload {files.length} Document{files.length !== 1 ? 's' : ''}
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

export default DocumentBulkUploadPage;
